/* eslint-disable react/jsx-no-bind */
import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import { Outlet, useParams } from 'react-router';
import { AccountSuspendedIndicator } from '~/components/Commons/AccountSuspendedIndicator/AccountSuspendedIndicator';
import DianomiSpinner from '~/components/Commons/DianomiSpinner/DianomiSpinner';
import MobileNavBar from '~/components/Commons/Navbars/MobileNavBar';
import { PageLoadingIndicator } from '~/components/Commons/PageLoadingIndicator/PageLoadingIndicator';
import Sidebar from '~/components/Commons/Sidebar/Sidebar';
import useMiniMenu from '~/context/useMiniMenu';
import useWindowSize from '~/hooks/useWindowSize';
import adminStyle from '~/layouts/Admin/adminStyle';
import sidebarRoutes from '~/sidebar-routes';
import ErrorBoundaryPage from '~/views/Pages/ErrorBoundaryPage';
import useGenieNavQuery from '../../hooks/queries/useGenieNavQuery';
import useSessionPlQuery from '../../hooks/queries/useSessionPlQuery';
import { getActiveRoute, setRoutesPermissions } from './RoutePermissions';

export default function Admin() {
  const { accountId, accountType } = useParams();
  const { isOpen, toggle } = useMiniMenu();
  const { isMobile } = useWindowSize();
  const { data: sessionData, isLoading } = useSessionPlQuery();
  const { data: genieNavPlData, isLoading: isGenieNavLoading } = useGenieNavQuery();
  const isNavDataLoading = isLoading || isGenieNavLoading;
  const processedRoutes = React.useMemo(() => {
    if (!sessionData) return [];
    return setRoutesPermissions(sidebarRoutes, {
      ...sessionData,
      genieNav: genieNavPlData,
      emptyAccounts: false,
    });
  }, [genieNavPlData, sessionData]);
  return (
    <Box sx={adminStyle.root} id="admin-root">
      {isMobile && (
        <Box
          sx={{
            gridArea: 'header',
            position: 'sticky',
            top: 0,
            zIndex: 3000,
          }}
        >
          {!isLoading && sessionData?.id.toString() === accountId ? (
            <>
              <AccountSuspendedIndicator />
              <MobileNavBar
                handleDrawerToggle={toggle}
                activeRoute={getActiveRoute(processedRoutes)}
                mobileNavOpen={isOpen}
              />
            </>
          ) : (
            <MobileNavBar isAuthBar />
          )}
        </Box>
      )}

      {processedRoutes.length > 0 && accountId && sessionData?.id.toString() === accountId && (
        <Box sx={{ gridArea: 'sidebar', maxWidth: '210px', height: '100vh', borderRadius: '4px' }}>
          <Sidebar
            sidebarMinimize={toggle}
            handleDrawerToggle={toggle}
            key={sessionData?.id}
            routes={processedRoutes}
            basePath={`/${accountType}/${accountId}`}
            isNavDataLoading={isNavDataLoading}
          />
        </Box>
      )}
      <Box sx={adminStyle.mainPanel}>
        {!isMobile && (
          <>
            <PageLoadingIndicator />
            <AccountSuspendedIndicator />
          </>
        )}
        <DianomiSpinner loading={isLoading}>
          <Sentry.ErrorBoundary
            // eslint-disable-next-line react/jsx-props-no-spreading
            fallback={(props) => <ErrorBoundaryPage {...props} />}
          >
            <React.Suspense fallback={<DianomiSpinner loading delay={500} />}>
              <Outlet />
            </React.Suspense>
          </Sentry.ErrorBoundary>
        </DianomiSpinner>
      </Box>
    </Box>
  );
}
